import { useCallback, useEffect } from 'react';

import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useForm } from 'react-hook-form';
import {
  IdentitySummaryResponse,
  IdentitySummaryResponseMfaTypeEnum,
  MfaControllerApi,
} from '../../openapi/keymaker';
import ErrorService from '../../services/ErrorService';
import { fetchKeymakerCurrentUser } from '../../slices/AuthSlice';
import { showSuccessToast } from '../../slices/ToastNotificationSlice';
import { useAppDispatch } from '../../slices/store';
import { getCommonsApiErrorMessage } from '../../utils/ErrorUtils';
import { getKeymakerConfiguration } from '../../utils/OpenapiConfigurationUtils';
import { cn } from '../../utils/classUtils';
import Button from '../Button';
import SimpleModal from '../Modals/SimpleModal';
import ControlledTextInput from '../inputs/ControlledTextInput';

interface FormData {
  code: number;
}

interface DeleteMFAModalProps {
  isOpen: boolean;
  onClose(): void;
  keymakerCurrentUser: IdentitySummaryResponse;
}

const DeleteMFAModal: React.FC<DeleteMFAModalProps> = ({
  isOpen,
  onClose,
  keymakerCurrentUser,
}) => {
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = useForm<FormData>();

  const isSmsTypeMFA =
    keymakerCurrentUser?.mfaType === IdentitySummaryResponseMfaTypeEnum.Sms;

  const sendMfaSms = useCallback(async () => {
    try {
      await new MfaControllerApi(await getKeymakerConfiguration()).sendMfaSms(
        keymakerCurrentUser.phoneNumber,
      );
    } catch (e: any) {
      ErrorService.notify(
        'Unable to send authentication code to phone number during delete authentication',
        e,
        {
          data: { phoneNumber: keymakerCurrentUser.phoneNumber },
        },
      );
    }
  }, [keymakerCurrentUser.phoneNumber]);

  useEffect(() => {
    if (isSmsTypeMFA) {
      sendMfaSms();
    }
  }, [isSmsTypeMFA, sendMfaSms]);

  const onSubmit = async (values: FormData) => {
    try {
      await new MfaControllerApi(await getKeymakerConfiguration()).deleteMfa({
        code: values.code,
      });
      await dispatch(fetchKeymakerCurrentUser());
      onClose();
      dispatch(
        showSuccessToast('Successfully deleted two factor authentication.'),
      );
    } catch (e: any) {
      setError('code', {
        message: getCommonsApiErrorMessage(e),
      });
      ErrorService.notifyIgnoreAuthErrors(
        'Unable to delete two factor authentication',
        e,
        {
          data: {
            code: values.code,
          },
        },
      );
    }
  };

  return (
    <SimpleModal
      isOpen={isOpen}
      size='large'
      hideIcon
      titleComponent={
        <div className='flex flex-1 justify-between items-center'>
          <div className='flex items-center'>
            <div className='mr-3'>
              <div
                className={cn(
                  'flex justify-center items-center w-10 h-10 bg-opacity-20 rounded-full p-1 bg-red-600 text-red-600',
                )}
              >
                <FontAwesomeIcon icon={faTrash} />
              </div>
            </div>
            <p className='font-zen-body font-bold text-zen-dark-9 text-base'>
              Delete Two-Factor Authentication
            </p>
          </div>
        </div>
      }
      onClose={onClose}
    >
      <div className='px-8 pb-4 space-y-4'>
        <p className='font-primary-regular text-gray-500'>
          By removing this protection, your account will be less secure. If you
          are sure, enter the code from your mobile device or authenticator app.
        </p>
        <ControlledTextInput<FormData, 'code'>
          control={control}
          name='code'
          type='number'
          placeholder='123456'
          rules={{
            required: 'Please enter authentication code',
            minLength: {
              value: 6,
              message: 'Must have 6 digit number',
            },
            maxLength: {
              value: 6,
              message: 'Must have 6 digit number',
            },
          }}
        />
        <div className='flex justify-between gap-4'>
          <Button
            label='Cancel'
            fullWidth
            variant='primary-outline'
            onPress={onClose}
            buttonSize='sm'
          />
          <Button
            label='Delete'
            variant='danger'
            fullWidth
            isDisabled={isSubmitting}
            isSubmitting={isSubmitting}
            onPress={handleSubmit(onSubmit)}
            buttonSize='sm'
          />
        </div>
      </div>
    </SimpleModal>
  );
};

export default DeleteMFAModal;
